import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GripVertical, Trash2, Plus, ArrowDown, Check, X, Clock, KeyRound, Zap, Asterisk } from 'lucide-react';
import { WorkflowStep, WorkflowInput, WorkflowRun, InputType, INPUT_TYPES, WORKFLOW, logoUrl } from './workflowData';
interface Props {
  steps: WorkflowStep[];
  inputs: WorkflowInput[];
  editing: boolean;
  runs: WorkflowRun[];
  onChangeStep: (id: string, patch: Partial<WorkflowStep>) => void;
  onRemoveStep: (id: string) => void;
  onAddStep: () => void;
  onChangeInput: (id: string, patch: Partial<WorkflowInput>) => void;
  onRemoveInput: (id: string) => void;
  onAddInput: () => void;
  onSave: () => void;
  onCancel: () => void;
}
export function WorkflowStepsTab({
  steps,
  inputs,
  editing,
  runs,
  onChangeStep,
  onRemoveStep,
  onAddStep,
  onChangeInput,
  onRemoveInput,
  onAddInput,
  onSave,
  onCancel
}: Props) {
  const lastRun = runs[0];
  return <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_300px] gap-6" data-id="element-5654" data-mp="3qedn1ajqt1hu">
      <div data-id="element-5655" data-mp="2y4u9h1fwrbdc">
        {/* ── Inputs: what a run must be given before it can start ── */}
        <section className="mb-7" data-id="element-5656" data-mp="2t83yutmvh7zb">
          <div className="flex items-center justify-between mb-3" data-id="element-5657" data-mp="2t4y694f1bnyu">
            <h2 className="font-title text-[17px] font-bold" data-id="element-5658" data-mp="2p44jiedbwf7j">
              {editing ? 'Required inputs' : 'Inputs'}
            </h2>
            <span className="font-mono text-[11px] text-neutral-500" data-id="element-5659" data-mp="2k8gxu8y2246s">
              {inputs.filter(i => i.required).length} required ·{' '}
              {inputs.length} total
            </span>
          </div>

          <div className="rounded-2xl border border-[#E7E3DD] bg-white divide-y divide-[#EFEBE5]" data-id="element-5660" data-mp="25hgbd00h49rn">
            {inputs.length === 0 && <p className="px-4 py-5 text-[13px] text-neutral-500" data-id="element-5661" data-mp="21gel38ojb81t">
                This workflow takes no inputs — it runs on a fixed set of
                instructions.
              </p>}

            {inputs.map(input => <div key={input.id} className="p-4" data-id="element-5662" data-mp="1rhfdzujf3kgx">
                {editing ? <div className="space-y-2.5" data-id="element-5663" data-mp="1hu2y84kqpy8w">
                    <div className="flex flex-wrap items-center gap-2" data-id="element-5664" data-mp="1hta1lwfhwp4h">
                      <input value={input.label} onChange={e => onChangeInput(input.id, {
                  label: e.target.value
                })} aria-label="Input label" placeholder="Label" className="flex-1 min-w-[140px] rounded-lg border border-[#E7E3DD] bg-[#FFFCF8] px-3 py-2 text-[14px] font-bold focus:outline-none focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/15" data-id="element-5665" data-mp="1mtpekmvv7ulx" />
                      <select value={input.type} onChange={e => onChangeInput(input.id, {
                  type: e.target.value as InputType
                })} aria-label="Input type" className="rounded-lg border border-[#E7E3DD] bg-[#FFFCF8] px-2.5 py-2 text-[13px] font-semibold text-[#5C544C] focus:outline-none focus:border-[#EA580C]" data-id="element-5666" data-mp="25je39y94iesu">
                        {INPUT_TYPES.map(t => <option key={t} value={t} data-id="element-5667" data-mp="2jhc3lzqm0apc">
                            {t}
                          </option>)}
                      </select>
                      <button type="button" onClick={() => onChangeInput(input.id, {
                  required: !input.required
                })} aria-pressed={input.required} className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-2 text-[12.5px] font-semibold transition-colors ${input.required ? 'border-[#EA580C] bg-[#FFF7ED] text-[#C2410C]' : 'border-[#E7E3DD] bg-white text-neutral-500 hover:bg-[#F6F5F3]'}`} data-id="element-5668" data-mp="cuej8myxa0ol">
                        <Asterisk size={12} data-id="element-5669" data-mp="3q1yedjz84m2v" />
                        {input.required ? 'Required' : 'Optional'}
                      </button>
                      <button type="button" onClick={() => onRemoveInput(input.id)} className="w-9 h-9 inline-flex items-center justify-center rounded-lg text-neutral-400 hover:bg-red-50 hover:text-red-600 transition-colors" aria-label={`Remove ${input.label}`} data-id="element-5670" data-mp="2wqohx1bgt6f">
                        <Trash2 size={14} data-id="element-5671" data-mp="1ee8ii4t3iefr" />
                      </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2" data-id="element-5672" data-mp="1acmxtxabkvv0">
                      <span className="font-mono text-[12px] text-neutral-400" data-id="element-5673" data-mp="25m1wtqzmomo1">
                        {'{{'}
                      </span>
                      <input value={input.key} onChange={e => onChangeInput(input.id, {
                  key: e.target.value.replace(/\s+/g, '_').toLowerCase()
                })} aria-label="Reference name" className="w-[150px] rounded-lg border border-[#E7E3DD] bg-[#FFFCF8] px-2.5 py-1.5 font-mono text-[12px] focus:outline-none focus:border-[#EA580C]" data-id="element-5674" data-mp="29o5amm9rs3ch" />
                      <span className="font-mono text-[12px] text-neutral-400" data-id="element-5675" data-mp="2sjve6dxcp7sh">
                        {'}}'}
                      </span>
                      <input value={input.hint} onChange={e => onChangeInput(input.id, {
                  hint: e.target.value
                })} aria-label="Helper text" placeholder="Helper text shown when a run is started" className="flex-1 min-w-[180px] rounded-lg border border-[#E7E3DD] bg-[#FFFCF8] px-3 py-1.5 text-[13px] text-[#5C544C] focus:outline-none focus:border-[#EA580C]" data-id="element-5676" data-mp="2xe94w312iqqt" />
                    </div>
                  </div> : <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1" data-id="element-5677" data-mp="vm7l4esooces">
                    <span className="text-[14px] font-bold" data-id="element-5678" data-mp="qnnzyglwz4nh">{input.label}</span>
                    <span className="rounded-full border border-[#E7E3DD] bg-[#F6F5F3] px-2 py-0.5 text-[11px] font-semibold text-[#6B6259]" data-id="element-5679" data-mp="qpqn8svomjoi">
                      {input.type}
                    </span>
                    {input.required ? <span className="rounded-full border border-[#FED7AA] bg-[#FFF7ED] px-2 py-0.5 text-[11px] font-bold text-[#C2410C]" data-id="element-5680" data-mp="1edi9ao70yb2z">
                        Required
                      </span> : <span className="rounded-full border border-[#E7E3DD] px-2 py-0.5 text-[11px] font-semibold text-neutral-500" data-id="element-5681" data-mp="19k5l2f69c6ar">
                        Optional
                      </span>}
                    <span className="font-mono text-[11.5px] text-neutral-400" data-id="element-5682" data-mp="224vywmdsjsk9">
                      {`{{${input.key}}}`}
                    </span>
                    <p className="w-full text-[13px] text-[#5C544C]" data-id="element-5683" data-mp="3rjoodja9lp0e">
                      {input.hint}
                    </p>
                  </div>}
              </div>)}
          </div>

          {editing && <button type="button" onClick={onAddInput} className="mt-2.5 inline-flex items-center gap-1.5 rounded-xl border border-[#E7E3DD] bg-white px-3.5 py-2 text-[13px] font-semibold text-[#5C544C] hover:border-[#EA580C] hover:text-[#EA580C] transition-colors" data-id="element-5684" data-mp="3huwvn8xdr0of">
              <Plus size={14} data-id="element-5685" data-mp="3cv289fctct84" />
              Add an input
            </button>}
        </section>

        {/* ── Steps ── */}
        <div className="flex items-center justify-between mb-3" data-id="element-5686" data-mp="2yw2k08t7qgch">
          <h2 className="font-title text-[17px] font-bold" data-id="element-5687" data-mp="2yxlwrpfnu99i">
            {editing ? 'Editing steps' : 'Steps'}
          </h2>
          <span className="font-mono text-[11px] text-neutral-500" data-id="element-5688" data-mp="x3eefst3jgtj">
            {steps.length} step{steps.length === 1 ? '' : 's'} · runs top to bottom
          </span>
        </div>

        <ol className="space-y-3" data-id="element-5689" data-mp="2ml1ja2tq87w8">
          {steps.map((step, i) => <li key={step.id} data-id="element-5690" data-mp="2mlb83n31c90c" data-mpi={i}>
              <motion.div layout initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.22
          }} className={`group rounded-2xl border bg-white p-4 md:p-5 transition-colors ${editing ? 'border-[#E7E3DD] hover:border-[#EA580C]/50' : 'border-[#E7E3DD]'}`} data-id="element-5691" data-mp="2hlynl8zmze0x">
                <div className="flex items-start gap-3" data-id="element-5692" data-mp="359onkj42wt8r">
                  {editing && <button type="button" className="mt-1 text-neutral-300 hover:text-neutral-500 cursor-grab" aria-label={`Reorder step ${i + 1}`} data-id="element-5693" data-mp="30bn1rqf9qftl">
                      <GripVertical size={16} data-id="element-5694" data-mp="3aamdjhvbyrkt" />
                    </button>}
                  <span className="mt-0.5 w-7 h-7 shrink-0 rounded-full bg-[#F6F5F3] border border-[#E7E3DD] flex items-center justify-center font-mono text-[12px] font-semibold text-[#6B6259]" data-id="element-5695" data-mp="3abdupno6kmrk">
                    {i + 1}
                  </span>

                  <div className="flex-1 min-w-0" data-id="element-5696" data-mp="u3xlv5sy82mv">
                    {editing ? <input value={step.title} onChange={e => onChangeStep(step.id, {
                  title: e.target.value
                })} aria-label={`Step ${i + 1} title`} className="w-full rounded-lg border border-[#E7E3DD] bg-[#FFFCF8] px-3 py-2 text-[15px] font-bold focus:outline-none focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/15" data-id="element-5697" data-mp="u1l52vyxfybe" /> : <h3 className="text-[15px] font-bold leading-snug" data-id="element-5698" data-mp="b5cy9nq9nfdz">
                        {step.title}
                      </h3>}

                    {editing ? <textarea value={step.body} onChange={e => onChangeStep(step.id, {
                  body: e.target.value
                })} rows={4} aria-label={`Step ${i + 1} instructions`} className="mt-2.5 w-full rounded-lg border border-[#E7E3DD] bg-[#FFFCF8] px-3 py-2.5 text-[13.5px] leading-relaxed text-[#4A423B] focus:outline-none focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/15 resize-y" data-id="element-5699" data-mp="g5qvsbu8r6xr" /> : <p className="mt-2 text-[13.5px] leading-relaxed text-[#5C544C]" data-id="element-5700" data-mp="20lknt6p7us9k">
                        {step.body}
                      </p>}

                    {editing && <div className="mt-2.5 flex items-center justify-between gap-3" data-id="element-5701" data-mp="3h1013366lm1a">
                        <p className="font-mono text-[11px] text-neutral-400 truncate" data-id="element-5702" data-mp="3c8f6hzfgb4wp">
                          Reference inputs with {'{{key}}'}
                        </p>
                        <button type="button" onClick={() => onRemoveStep(step.id)} className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12px] font-semibold text-neutral-500 hover:bg-red-50 hover:text-red-600 transition-colors shrink-0" data-id="element-5703" data-mp="32asqtaqbv9bo">
                          <Trash2 size={13} data-id="element-5704" data-mp="2tamevtaa2hem" />
                          Remove step
                        </button>
                      </div>}
                  </div>
                </div>
              </motion.div>

              {i < steps.length - 1 && <div className="flex justify-center py-1.5" aria-hidden="true" data-id="element-5705" data-mp="mk66vpbw4dd8">
                  <ArrowDown size={14} className="text-neutral-300" data-id="element-5706" data-mp="mi263g9afuh6" />
                </div>}
            </li>)}
        </ol>

        {editing && <button type="button" onClick={onAddStep} className="mt-3 w-full rounded-2xl border-2 border-dashed border-[#E0DBD3] py-3.5 text-[13.5px] font-semibold text-[#6B6259] hover:border-[#EA580C] hover:text-[#EA580C] hover:bg-[#FFF7ED] transition-colors inline-flex items-center justify-center gap-2" data-id="element-5707" data-mp="2k010mrulszv5">
            <Plus size={15} data-id="element-5708" data-mp="259tr51mkqr6l" />
            Add a step
          </button>}
      </div>

      {/* Right rail — the operational facts you want while reading the steps. */}
      <aside className="space-y-4" data-id="element-5709" data-mp="2f0eiq53tyvg0">
        <RailCard title="Trigger" data-id="element-5710" data-mp="2f1g7tdholigq">
          <p className="text-[13px] leading-relaxed text-[#5C544C]" data-id="element-5711" data-mp="2f1pqeg0ecnce">
            {WORKFLOW.triggerLabel}
          </p>
        </RailCard>

        <RailCard title="Credentials used" data-id="element-5712" data-mp="1mju4smi4bq6v">
          <ul className="space-y-2.5" data-id="element-5713" data-mp="1miiz2h5448d4">
            {[{
            name: 'X — OAuth',
            meta: 'x-user-2',
            domain: 'x.com'
          }, {
            name: 'X — Browser session',
            meta: 'fallback',
            domain: 'x.com'
          }, {
            name: 'Notion — MCP',
            meta: 'mcp-notion-claude',
            domain: 'notion.so'
          }].map(cred => <li key={cred.name} className="flex items-center gap-2.5" data-id="element-5714" data-mp="3k8z74ncp8egj">
                <img src={logoUrl(cred.domain)} alt="" className="w-5 h-5 rounded object-contain" data-id="element-5715" data-mp="3p5zgb74uhxbv" />
                <span className="flex-1 min-w-0" data-id="element-5716" data-mp="gqphc7w9588b">
                  <span className="block text-[13px] font-semibold truncate" data-id="element-5717" data-mp="gouu2mxid412">
                    {cred.name}
                  </span>
                  <span className="block font-mono text-[11px] text-neutral-500 truncate" data-id="element-5718" data-mp="6tb1oai5knh2">
                    {cred.meta}
                  </span>
                </span>
                <KeyRound size={13} className="text-neutral-300 shrink-0" data-id="element-5719" data-mp="btpc1z7889i9" />
              </li>)}
          </ul>
        </RailCard>

        <RailCard title="Reliability" data-id="element-5720" data-mp="2whtro9ao7o6r">
          <dl className="space-y-2.5" data-id="element-5721" data-mp="2wjogvzsz62gn">
            <Stat label="Success rate" value={WORKFLOW.successRate} data-id="element-5722" data-mp="2cfzbcprnxswa" />
            <Stat label="Avg. duration" value={WORKFLOW.avgDuration} data-id="element-5723" data-mp="2cf7e7aif3n3w" />
            <Stat label="Total runs" value={String(WORKFLOW.totalRuns)} data-id="element-5724" data-mp="28fgicse1agsi" />
          </dl>
          {lastRun && <p className="mt-3 pt-3 border-t border-[#EFEBE5] flex items-center gap-1.5 text-[12px] text-neutral-500" data-id="element-5725" data-mp="23mnd83enl6fw">
              <Clock size={12} data-id="element-5726" data-mp="1yo2ksq5n7r20" />
              Last run {lastRun.relative}
            </p>}
        </RailCard>
      </aside>

      {/* Sticky save bar — only while editing. */}
      <AnimatePresence data-id="element-5727" data-mp="1opd3ayhns8ma">
        {editing && <motion.div initial={{
        y: 70,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 70,
        opacity: 0
      }} transition={{
        type: 'spring',
        stiffness: 380,
        damping: 34
      }} className="fixed inset-x-0 bottom-0 z-30 border-t border-[#E7E3DD] bg-white/95 backdrop-blur-sm shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.12)]" data-id="element-5728" data-mp="1op3evfc9i9ip">
            <div className="max-w-5xl mx-auto px-6 py-3.5 flex items-center gap-3" data-id="element-5729" data-mp="3a1woe7n9x60u">
              <Zap size={15} className="text-[#EA580C] shrink-0" data-id="element-5730" data-mp="3hn0airwwdqiw" />
              <p className="flex-1 text-[13px] text-[#5C544C]" data-id="element-5731" data-mp="3mlbjl0fdbx02">
                Changes apply to the next run. Runs already recorded keep the
                inputs and steps they executed.
              </p>
              <button type="button" onClick={onCancel} className="inline-flex items-center gap-1.5 rounded-xl border border-[#E7E3DD] px-3.5 py-2 text-[13px] font-semibold text-[#5C544C] hover:bg-[#F6F5F3] transition-colors" data-id="element-5732" data-mp="5h2l78opg42">
                <X size={14} data-id="element-5733" data-mp="33plfn7nvkqjj" />
                Cancel
              </button>
              <button type="button" onClick={onSave} className="inline-flex items-center gap-1.5 rounded-xl bg-[#2B211A] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#EA580C] transition-colors" data-id="element-5734" data-mp="38l0pzpmoc4b7">
                <Check size={14} data-id="element-5735" data-mp="nw38osz4oqbf" />
                Save workflow
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function RailCard({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="rounded-2xl border border-[#E7E3DD] bg-white p-4" data-id="element-5736" data-mp="lihlw9yu30pd">
      <h3 className="mb-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-neutral-400" data-id="element-5737" data-mp="10eys6ms61p52">
        {title}
      </h3>
      {children}
    </section>;
}
function Stat({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="flex items-baseline justify-between gap-3" data-id="element-5738" data-mp="19elowv9ya6rs">
      <dt className="text-[13px] text-[#5C544C]" data-id="element-5739" data-mp="14gtthra3k2iu">{label}</dt>
      <dd className="font-title text-[15px] font-bold" data-id="element-5740" data-mp="14gaeaibtzs8i">{value}</dd>
    </div>;
}